import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { compose, lifecycle } from "recompose";
import { Messages } from "@worcket/common/components/listing";
import * as R from "ramda";
import styled from "styled-components";
import { Icon, Input } from "antd";

import i18n from "../../i18n";
import chatbotAvatar from "../../assets/chatbot/chatbotAvatar.svg";
import { messageRead, sendMessage, sendFile } from "../../store/actions/chat";
import { FETCH_MATCH_MESSAGES } from "../../store/actions/candidates";
import { isLoading } from "../../utils/actions";
import { getOptimizedImage } from "../../utils/images";
import { ACCEPT_LANGUAGE } from "../../locales/acceptedLanguages";

import CalendarMessage from "./CalendarMessage";
import HumanTestMessage from "./HumanTestMessage";
import JobOfferMessage from "./JobOfferMessage";

const locale = ACCEPT_LANGUAGE[i18n.language] || ACCEPT_LANGUAGE.DEFAULT;

const isMobileViewport = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(max-width: 767px)").matches;

const ChatWrapper = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  position: relative;
  width: 100%;

  > div {
    min-height: 0;
  }

  @media (max-width: 1024px) {
    overflow: hidden;
    height: 100%;

    > div,
    > div > div,
    #messages,
    #messages .scrollable {
      box-sizing: border-box;
      max-width: 100%;
      min-width: 0;
      width: 100% !important;
      padding-top: 0px;
    }

    > div {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    #messages {
      flex: 1 1 auto;
      min-height: 0;
      overflow-x: hidden;
      border-right: 1px solid #dee2e6;
    }

    #messages .day-divider,
    #messages .right,
    #messages .left {
      box-sizing: border-box;
      max-width: 100%;
      width: 100% !important;
    }

    #messages .right > div,
    #messages .left > div,
    #messages .message-with-time-wrapper {
      min-width: 0;
      max-width: 100%;
    }

    #messages .message {
      box-sizing: border-box;
      max-width: min(82%, calc(100% - 56px)) !important;
      overflow-wrap: anywhere;
      word-break: break-word;
    }

    #messages .button {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    #messages .button span {
      display: inline-block;
      max-width: calc(100% - 72px);
      overflow: hidden;
      text-overflow: ellipsis;
      vertical-align: bottom;
      white-space: nowrap;
    }

    > div > div:last-child {
      align-items: center;
      border-top: 1px solid #e5e9ed;
      flex: 0 0 auto;
      gap: 12px;
      padding: 16px;
      width: 100% !important;
    }

    > div > div:last-child textarea {
      min-width: 0;
    }
  }
  @media (min-width: 1024px) {
    #messages .left .message,
    #messages .right .message {
      box-sizing: border-box;
      max-width: 75% !important;
      overflow-wrap: anywhere;
    }
  }
`;

const AttachmentSheet = styled.div`
  .attachment-backdrop {
    background: rgba(0, 13, 68, 0.64);
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1001;
  }

  .attachment-panel {
    background: #ffffff;
    border-radius: 16px 16px 0 0;
    bottom: 0;
    box-shadow: 0 -12px 32px rgba(0, 13, 68, 0.16);
    left: 0;
    padding: 16px 16px 32px;
    position: fixed;
    right: 0;
    z-index: 1002;
  }

  .attachment-handle {
    background: #c4cad4;
    border-radius: 4px;
    height: 4px;
    margin: 0 auto 18px;
    width: 40px;
  }

  .attachment-header {
    align-items: center;
    color: #000d44;
    display: flex;
    font-size: 24px;
    font-weight: 700;
    justify-content: space-between;
    line-height: 1.2;
    margin-bottom: 22px;
  }

  .attachment-close {
    background: transparent;
    border: 0;
    color: #000d44;
    font-size: 24px;
    padding: 0;
  }

  .attachment-option,
  .attachment-cancel {
    align-items: center;
    background: #ffffff;
    border: 1px solid #dbe3ef;
    border-radius: 8px;
    color: #000d44;
    display: flex;
    font-size: 16px;
    font-weight: 600;
    height: 64px;
    padding: 0 24px;
    width: 100%;
  }

  .attachment-option + .attachment-option {
    margin-top: 8px;
  }

  .attachment-option .anticon {
    font-size: 26px;
    margin-right: 24px;
  }

  .attachment-cancel {
    color: #004cff;
    justify-content: center;
    margin-top: 128px;
  }
`;

const ResponsiveMessages = props => {
  const { candidate, company, opportunity, onSend, onSendFile, t } = props;
  const [sheetVisible, setSheetVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const wrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) return undefined;

    const onAttachmentClick = event => {
      const button = event.target.closest && event.target.closest("button");

      if (
        !button ||
        !button.querySelector(".anticon-paper-clip") ||
        !isMobileViewport()
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setSheetVisible(true);
    };

    wrapper.addEventListener("click", onAttachmentClick, true);

    return () => wrapper.removeEventListener("click", onAttachmentClick, true);
  }, []);

  const sendSelectedFile = useCallback(
    async event => {
      try {
        if (event.target.files.length > 0) {
          const file = event.target.files[0];
          setInputValue("");
          setSheetVisible(false);
          const url = await onSendFile({
            file,
            company,
            opportunity,
            candidate
          });

          if (url) {
            onSend(`link:${file.name}:${url}`, {
              candidate,
              company,
              opportunity
            });
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    [candidate, company, onSend, onSendFile, opportunity]
  );

  const openInput = inputRef => {
    setSheetVisible(false);
    inputRef.current.input.click();
  };

  return (
    <ChatWrapper ref={wrapperRef}>
      <Messages {...props} />
      <Input
        hidden
        type="file"
        value={inputValue}
        ref={fileInputRef}
        onChange={sendSelectedFile}
      />
      <Input
        hidden
        accept="image/*"
        capture="environment"
        type="file"
        value={inputValue}
        ref={cameraInputRef}
        onChange={sendSelectedFile}
      />
      <Input
        hidden
        accept="image/*"
        type="file"
        value={inputValue}
        ref={galleryInputRef}
        onChange={sendSelectedFile}
      />
      {sheetVisible && (
        <AttachmentSheet>
          <div
            className="attachment-backdrop"
            onClick={() => setSheetVisible(false)}
          />
          <div className="attachment-panel" role="dialog" aria-modal="true">
            <div className="attachment-handle" />
            <div className="attachment-header">
              <span>{t("common:chat.attachFile")}</span>
              <button
                className="attachment-close"
                type="button"
                onClick={() => setSheetVisible(false)}
              >
                <Icon type="close" />
              </button>
            </div>
            <button
              className="attachment-option"
              type="button"
              onClick={() => openInput(cameraInputRef)}
            >
              <Icon type="camera" />
              {t("common:chat.takePhoto")}
            </button>
            <button
              className="attachment-option"
              type="button"
              onClick={() => openInput(galleryInputRef)}
            >
              <Icon type="picture" />
              {t("common:chat.selectFromGallery")}
            </button>
            <button
              className="attachment-option"
              type="button"
              onClick={() => openInput(fileInputRef)}
            >
              <Icon type="file-text" />
              {t("common:chat.selectFile")}
            </button>
            <button
              className="attachment-cancel"
              type="button"
              onClick={() => setSheetVisible(false)}
            >
              {t("common:chat.cancel")}
            </button>
          </div>
        </AttachmentSheet>
      )}
    </ChatWrapper>
  );
};

const mapState = state => {
  const idOpportunity = R.path(["opportunity", "selected", "id"], state);
  const idCandidate = R.path(["candidates", "selected", "id"], state);
  const candidate = R.path(["candidates", "selected"], state);
  const idCompany = R.path(["company", "id"], state);
  const avatarCompany = getOptimizedImage(R.path(["company", "logo"], state));
  const avatarCandidate = getOptimizedImage(
    R.path(["candidates", "selected", "avatar"], state)
  );

  const messages = R.pipe(
    R.pathOr([], ["candidates", "selected", "messages"]),
    R.chain(message => {
      const expandedMessages = [message];

      if (message.templateFile) {
        expandedMessages.push({
          ...message,
          key: `${message.timestamp}-template-file`,
          text: `link:${message.templateFileName || message.templateFile}:${
            message.templateFile
          }`,
          widgetMetadata: undefined,
          templateFile: undefined,
          templateFileName: undefined
        });
      }

      if (!R.has("widgetMetadata", message)) return expandedMessages;

      return [
        ...expandedMessages,
        {
          timestamp: message.timestamp,
          message: message,
          idCompany: idCompany,
          idOpportunity: idOpportunity,
          idCandidate: idCandidate,
          candidate,
          key: `custom-${message.timestamp}`,
          CustomRender: R.cond([
            [
              R.pathEq(["widgetMetadata", "widgetType"], "offer-letter"),
              R.always(JobOfferMessage)
            ],
            [
              R.pathEq(["widgetMetadata", "widgetType"], "test-gh"),
              R.always(HumanTestMessage)
            ],
            [
              R.pathEq(["widgetMetadata", "widgetType"], "test"),
              R.always(HumanTestMessage)
            ],
            [R.T, R.always(CalendarMessage)]
          ])(message)
        }
      ];
    })
  )(state);

  return {
    t: i18n.t.bind(i18n),
    locale: locale,
    candidate: idCandidate,
    company: idCompany,
    opportunity: idOpportunity,
    avatars: {
      candidate: avatarCandidate,
      company: avatarCompany,
      chatbot: chatbotAvatar
    },
    messages: messages.map(message => ({
      ...message,
      subtext: message.chatbot && i18n.t("chatMessages:automatedMessage")
    })),
    loading: isLoading(FETCH_MATCH_MESSAGES, state),
    sender: "company"
  };
};

const mapDispatch = {
  onSend: sendMessage,
  onRead: messageRead,
  onSendFile: sendFile
};

const enhancer = compose(
  connect(
    mapState,
    mapDispatch
  ),
  lifecycle({
    componentDidMount() {
      const { onRead, candidate, opportunity, company } = this.props;

      candidate &&
        opportunity &&
        onRead({
          idOpportunity: opportunity,
          idCandidate: candidate,
          idCompany: company
        });
    }
  })
);

export default enhancer(ResponsiveMessages);
